import Swal from "sweetalert2";
import { config } from "@/config.js";
import { sortBy, uniqBy } from "lodash";
import EventSource from "eventsource";

let BASE_URL = "";
config.loadConfig().then((configResult) => {
  BASE_URL = configResult.api;
});

export default {
  namespaced: true,
  state: {
    isCanceled: false,
    chatHistory: {
      loading: false,
      pagination: {
        total: 0,
        page: 0,
        itemsPerPage: 0,
        lastPage: null,
        data: [],
      },
    },
    chatStates: {}, // { [chatId]: { messages, loading, isWaitingForResponse, aiThinkingText, nextCursor, limit } }
    selectedChat: null,
    isCreatingChat: false,
    isTranscribing: false,
    patientMode: null, // Full patient object or null
  },
  getters: {
    currentChatState: (state) => {
      if (!state.selectedChat) return null;
      return state.chatStates[state.selectedChat.id] || null;
    },
    currentMessages: (state, getters) => {
      return getters.currentChatState?.messages || [];
    },
    currentMessagesLoading: (state, getters) => {
      return getters.currentChatState?.loading || false;
    },
    currentIsWaitingForResponse: (state, getters) => {
      return getters.currentChatState?.isWaitingForResponse || false;
    },
    currentIsStreamingContent: (state, getters) => {
      return getters.currentChatState?.isStreamingContent || false;
    },
    currentAiThinkingText: (state, getters) => {
      return getters.currentChatState?.aiThinkingText || null;
    },
    currentNextCursor: (state, getters) => {
      return getters.currentChatState?.nextCursor || null;
    },
  },
  mutations: {
    SET_CHAT_HISTORY_LOADING: (state, data) => {
      state.chatHistory.loading = data;
    },
    SET_CHAT_HISTORY_PAGINATION: (state, data) => {
      state.chatHistory.pagination = data;
    },
    INITIALIZE_CHAT_STATE: (state, chatId) => {
      if (!state.chatStates[chatId]) {
        state.chatStates[chatId] = {
          messages: [],
          loading: false,
          isWaitingForResponse: false,
          isStreamingContent: false,
          aiThinkingText: null,
          nextCursor: null,
          limit: null,
        };
      }
    },
    SET_CHAT_MESSAGES_LOADING: (state, { chatId, loading }) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].loading = loading;
    },
    SET_CHAT_MESSAGES_PAGINATION: (
      state,
      { chatId, nextCursor, limit, data }
    ) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].messages = data;
      state.chatStates[chatId].nextCursor = nextCursor;
      state.chatStates[chatId].limit = limit;
    },
    SET_SELECTED_CHAT: (state, data) => {
      state.selectedChat = data;
    },
    SET_WAITING_FOR_RESPONSE: (state, { chatId, value }) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].isWaitingForResponse = value;
    },
    SET_STREAMING_CONTENT: (state, { chatId, value }) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].isStreamingContent = value;
    },
    SET_CREATING_CHAT: (state, value) => {
      state.isCreatingChat = value;
    },
    SET_TRANSCRIBING: (state, value) => {
      state.isTranscribing = value;
    },
    SET_AI_THINKING_TEXT: (state, { chatId, value }) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].aiThinkingText = value;
    },
    STORE_CHAT_MESSAGE: (state, { chatId, message }) => {
      if (!state.chatStates[chatId]) return;
      const messages = state.chatStates[chatId].messages;
      const messageIndex = messages.findIndex((msg) => msg.id == message.id);
      if (messageIndex == -1) {
        const messageWithStatus = {
          ...message,
          status: message.role === "user" ? "pending" : "sent",
        };
        state.chatStates[chatId].messages = [...messages, messageWithStatus];
      }
    },
    SET_MESSAGE_STATUS: (
      state,
      { chatId, messageId, status, error = null }
    ) => {
      if (!state.chatStates[chatId]) return;
      const messages = state.chatStates[chatId].messages;
      const messageIndex = messages.findIndex((msg) => msg.id === messageId);

      if (messageIndex !== -1) {
        messages[messageIndex].status = status;
        if (error) {
          messages[messageIndex].error = error;
        }
      }
    },
    UPDATE_CHAT_MESSAGE: (state, { chatId, message }) => {
      if (!state.chatStates[chatId]) return;
      const messages = state.chatStates[chatId].messages;
      const messageIndex = messages.findIndex((msg) => msg.id == message.id);

      if (messageIndex === -1) {
        // eslint-disable-next-line no-unused-vars
        const { replace, ...messageData } = message;
        messages.push({
          ...messageData,
          content: messageData.content || "",
        });
        state.chatStates[chatId].messages = [...messages];
        return;
      }

      const existingMessage = messages[messageIndex];

      if (message.replace) {
        // eslint-disable-next-line no-unused-vars
        const { replace, ...updates } = message;
        messages[messageIndex] = {
          ...existingMessage,
          ...updates,
          content:
            updates.content !== undefined
              ? updates.content
              : existingMessage.content,
        };
      } else {
        const deltaContent = message.content || "";
        messages[messageIndex] = {
          ...existingMessage,
          content: (existingMessage.content || "") + deltaContent,
        };
      }
      state.chatStates[chatId].messages = [...messages];
    },
    DELETE_CHAT_MESSAGE: (state, { chatId, messageId }) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].messages = state.chatStates[
        chatId
      ].messages.filter((msg) => msg.id != messageId);
    },
    REMOVE_CHAT_FROM_HISTORY: (state, chatId) => {
      state.chatHistory.pagination.data =
        state.chatHistory.pagination.data.filter((chat) => chat.id !== chatId);
      // Update total count
      if (state.chatHistory.pagination.total > 0) {
        state.chatHistory.pagination.total -= 1;
      }
      // Clear selected chat if it was deleted
      if (state.selectedChat?.id === chatId) {
        state.selectedChat = null;
      }
      // Clean up chat state
      if (state.chatStates[chatId]) {
        delete state.chatStates[chatId];
      }
    },
    SET_CHAT_TITLE: (state, { chatId, title }) => {
      const chatIndex = state.chatHistory.pagination.data.findIndex(
        (chat) => chat.id == chatId
      );
      if (chatIndex != -1) {
        state.chatHistory.pagination.data[chatIndex].title = title;
      }
      state.chatHistory.pagination.data = [
        ...state.chatHistory.pagination.data,
      ];

      if (state.selectedChat && state.selectedChat.id == chatId) {
        state.selectedChat = {
          ...state.selectedChat,
          title: title,
        };
      }
    },
    RESET_CHAT_MESSAGES: (state, chatId) => {
      if (chatId && state.chatStates[chatId]) {
        delete state.chatStates[chatId];
      } else {
        state.chatStates = {};
      }
    },
    SET_PATIENT_MODE: (state, data) => {
      state.patientMode = data;
    },
  },
  actions: {
    getChats: function ({ commit, state }, payload) {
      const vm = this;
      commit("SET_CHAT_HISTORY_LOADING", true);
      let url = `assistant-conversations?`;
      if (payload) {
        if (payload.limit) {
          url = `${url}limit=${payload.limit}`;
        }
        if (payload.page) {
          url = `${url}&page=${payload.page}`;
        }
        if (payload.title) {
          url = `${url}&title=${payload.title}`;
        }
      }
      return new Promise((resolve) => {
        return vm.$http.get(url).then(
          (res) => {
            if (payload.limit) {
              if (payload.page == 1) {
                commit("SET_CHAT_HISTORY_PAGINATION", {
                  total: res.data.meta.total,
                  page: res.data.meta.current_page,
                  itemsPerPage: res.data.meta.per_page,
                  lastPage: res.data.meta.last_page,
                  data: res.data.data,
                });
              } else {
                commit("SET_CHAT_HISTORY_PAGINATION", {
                  total: res.data.meta.total,
                  page: res.data.meta.current_page,
                  itemsPerPage: res.data.meta.per_page,
                  lastPage: res.data.meta.last_page,
                  data: uniqBy(
                    [...state.chatHistory.pagination.data, ...res.data.data],
                    "id"
                  ),
                });
              }
            } else {
              commit("SET_CHAT_HISTORY_PAGINATION", {
                total: 0,
                page: 0,
                itemsPerPage: 0,
                lastPage: null,
                data: res.data.data,
              });
            }
            commit("SET_CHAT_HISTORY_LOADING", false);
            resolve(res);
          },
          (err) => {
            commit("SET_CHAT_HISTORY_LOADING", false);
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: err?.data?.error?.message || "Something went wrong...",
                icon: "error",
              });
            }
            resolve(false);
          }
        );
      });
    },
    createChat: function ({ commit }) {
      const vm = this;
      commit("SET_CREATING_CHAT", true);
      return new Promise((resolve) => {
        return vm.$http.post("assistant-conversations").then(
          (res) => {
            commit("SET_CREATING_CHAT", false);
            resolve(res);
          },
          (err) => {
            commit("SET_CREATING_CHAT", false);
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: err?.data?.error?.message || "Something went wrong...",
                icon: "error",
              });
            }
            resolve(false);
          }
        );
      });
    },
    getChatMessages: function ({ commit, state }, payload) {
      const chatId = payload.chatId;
      commit("INITIALIZE_CHAT_STATE", chatId);
      commit("SET_CHAT_MESSAGES_LOADING", { chatId, loading: true });

      let url = `assistant-conversations/${chatId}/messages?`;
      if (payload) {
        if (payload.limit) {
          url = `${url}limit=${payload.limit}`;
        }
        if (payload.cursor) {
          url = `${url}&cursor=${payload.cursor}`;
        }
      }
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http.get(url).then(
          (res) => {
            const chatState = state.chatStates[chatId];
            if (payload.limit) {
              if (payload.cursor) {
                commit("SET_CHAT_MESSAGES_PAGINATION", {
                  chatId,
                  nextCursor: res.data.meta.next_cursor,
                  limit: res.data.meta.per_page,
                  data: [
                    ...sortBy([...res.data.data], ["created_at"]),
                    ...(chatState?.messages || []),
                  ],
                });
              } else {
                commit("SET_CHAT_MESSAGES_PAGINATION", {
                  chatId,
                  nextCursor: res.data.meta.next_cursor,
                  limit: res.data.meta.per_page,
                  data: sortBy(res.data.data, ["created_at"]),
                });
              }
            } else {
              commit("SET_CHAT_MESSAGES_PAGINATION", {
                chatId,
                nextCursor: null,
                limit: null,
                data: res.data.data,
              });
            }
            commit("SET_CHAT_MESSAGES_LOADING", { chatId, loading: false });
            resolve(res);
          },
          (err) => {
            commit("SET_CHAT_MESSAGES_LOADING", { chatId, loading: false });
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: err?.data?.error?.message || "Something went wrong...",
                icon: "error",
              });
            }
            resolve(false);
          }
        );
      });
    },
    sendChatMessage: function ({ commit, state }, payload) {
      const chatId = payload.chatId;
      const userMessageId = payload.userMessageId;
      commit("INITIALIZE_CHAT_STATE", chatId);
      commit("SET_WAITING_FOR_RESPONSE", { chatId, value: true });
      commit("SET_STREAMING_CONTENT", { chatId, value: false });
      const vm = this;

      const hasFiles = payload.data.files?.length > 0;

      const mode = state.patientMode?.id ? "patient" : null;
      const contextMetadata = state.patientMode?.id
        ? {
            patient_id: state.patientMode.id,
            patient_name: `${state.patientMode.first || ""} ${
              state.patientMode.last || ""
            }`.trim(),
          }
        : null;

      let requestData;
      let config = {};

      if (hasFiles) {
        const formData = new FormData();
        formData.append("content", payload.data.content || "");

        if (mode) {
          formData.append("mode", mode);
        }

        if (contextMetadata) {
          formData.append("context_metadata", JSON.stringify(contextMetadata));
        }

        if (payload.webSearch !== undefined) {
          formData.append("web_search", payload.webSearch ? "1" : "0");
        }

        payload.data.files.forEach((file, index) => {
          formData.append(`attachments[${index}]`, file, file.name);
        });

        requestData = formData;
        config.headers = { "Content-Type": "multipart/form-data" };
      } else {
        requestData = {
          content: payload.data.content,
          ...(mode && { mode }),
          ...(contextMetadata && { context_metadata: contextMetadata }),
          ...(payload.webSearch !== undefined && {
            web_search: payload.webSearch,
          }),
        };
      }

      return new Promise((resolve) => {
        return vm.$http
          .post(
            `assistant-conversations/${chatId}/messages`,
            requestData,
            config
          )
          .then(
            (res) => {
              commit("SET_MESSAGE_STATUS", {
                chatId,
                messageId: userMessageId,
                status: "sent",
              });
              resolve(res);
            },
            (err) => {
              commit("SET_WAITING_FOR_RESPONSE", { chatId, value: false });
              commit("SET_MESSAGE_STATUS", {
                chatId,
                messageId: userMessageId,
                status: "failed",
                error: err?.data?.error?.message || "Failed to send message",
              });
              resolve(false);
            }
          );
      });
    },
    establishSSEConnection: function ({ commit }, { chatId, userMessageId }) {
      commit("INITIALIZE_CHAT_STATE", chatId);
      const token = localStorage.getItem("ARSS_TOKEN");

      const assistantMessageId = `assistant-${Date.now()}`;

      return new Promise((resolve) => {
        const evtSource = new EventSource(
          `${BASE_URL}assistant-conversations/${chatId}/sse`,
          {
            headers: {
              Authorization: "Bearer " + token,
            },
          }
        );

        evtSource.addEventListener("metadata", function () {
          commit("SET_AI_THINKING_TEXT", {
            chatId,
            value: "Request received, preparing response...",
          });
        });

        evtSource.addEventListener("thinking", function (event) {
          const data = JSON.parse(event.data) || {};
          const thinkingText = data.content || null;

          if (thinkingText && thinkingText.trim() !== "") {
            commit("SET_STREAMING_CONTENT", { chatId, value: false });
            commit("SET_AI_THINKING_TEXT", { chatId, value: thinkingText });
          }
        });

        evtSource.addEventListener("content", function (event) {
          const data = JSON.parse(event.data) || {};
          const content = data.content || null;

          if (content) {
            // Mark content as streaming and hide thinking indicator
            commit("SET_STREAMING_CONTENT", { chatId, value: true });
            commit("SET_AI_THINKING_TEXT", { chatId, value: null });

            commit("UPDATE_CHAT_MESSAGE", {
              chatId,
              message: {
                id: assistantMessageId,
                role: "assistant",
                content,
                created_at: new Date().toISOString(),
              },
            });
          }
        });

        evtSource.addEventListener("complete", function () {
          commit("SET_WAITING_FOR_RESPONSE", { chatId, value: false });
          commit("SET_STREAMING_CONTENT", { chatId, value: false });
          commit("SET_AI_THINKING_TEXT", { chatId, value: null });
          evtSource.close();
          resolve(true);
        });

        evtSource.onerror = function (event) {
          evtSource.close();
          commit("SET_WAITING_FOR_RESPONSE", { chatId, value: false });
          commit("SET_STREAMING_CONTENT", { chatId, value: false });
          commit("SET_AI_THINKING_TEXT", { chatId, value: null });

          // Mark user message as failed - visual indicator is sufficient feedback
          if (userMessageId) {
            commit("SET_MESSAGE_STATUS", {
              chatId,
              messageId: userMessageId,
              status: "failed",
              error: "Failed to receive AI response",
            });
          }

          console.error("SSE connection error:", event);
          resolve(false);
        };
      });
    },
    generateChatTitle: function ({ commit }, chatId) {
      const vm = this;
      return new Promise((resolve) => {
        return vm.$http
          .post(`assistant-conversations/${chatId}/generate-title`)
          .then(
            (res) => {
              commit("SET_CHAT_TITLE", {
                chatId: chatId,
                title: res.data.data.title,
              });
              resolve(res);
            },
            () => {
              resolve(false);
            }
          );
      });
    },
    transcribeAudio: function ({ commit }, payload) {
      const vm = this;
      commit("SET_TRANSCRIBING", true);
      const formData = new FormData();
      formData.append("audio", payload.file.blob, payload.file.name);
      return new Promise((resolve) => {
        vm.$http
          .post("assistant-conversations/transcribe", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(
            (res) => {
              commit("SET_TRANSCRIBING", false);
              resolve(res.data);
            },
            (err) => {
              commit("SET_TRANSCRIBING", false);
              if (!err.accessDenied) {
                Swal.fire({
                  title: "Error",
                  text: err?.data?.error?.message || "Something went wrong...",
                  icon: "error",
                });
              }
              resolve(false);
            }
          );
      });
    },
    setPatientMode: function ({ commit }, mode) {
      commit("SET_PATIENT_MODE", mode);
    },
    setSelectedChat: function ({ commit }, payload) {
      commit("SET_SELECTED_CHAT", payload);
    },
    storeChatMessage: function ({ commit, state }, payload) {
      const chatId = payload.chatId;
      commit("INITIALIZE_CHAT_STATE", chatId);
      commit("STORE_CHAT_MESSAGE", {
        chatId,
        message: payload.message,
      });
      return state.chatStates[chatId]?.messages?.at(-1);
    },
    updateChatMessage: function ({ commit }, payload) {
      const chatId = payload.chatId;
      commit("UPDATE_CHAT_MESSAGE", {
        chatId,
        message: {
          ...payload.updates,
          replace: true,
        },
      });
    },
    deleteChat: function ({ commit }, chatId) {
      const vm = this;
      return new Promise((resolve, reject) => {
        return vm.$http.delete(`assistant-conversations/${chatId}`).then(
          (res) => {
            commit("REMOVE_CHAT_FROM_HISTORY", chatId);
            resolve(res);
          },
          (err) => {
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text:
                  err?.data?.error?.message ||
                  "Failed to delete chat. Please try again.",
                icon: "error",
              });
            }
            reject(false);
          }
        );
      });
    },
    resetChatMessages: function ({ commit }, chatId) {
      commit("RESET_CHAT_MESSAGES", chatId);
    },
  },
};
